// Thin API client for the Flow Forge portal.
// All requests use credentials: 'include' so the HttpOnly session cookie flows.
const API = process.env.REACT_APP_BACKEND_URL;

function readCookie(name) {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

async function request(path, { method = 'GET', body, csrf = false } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (csrf) {
    const token = readCookie('ffm_csrf');
    if (token) headers['X-CSRF-Token'] = token;
  }
  const res = await fetch(`${API}${path}`, {
    method,
    headers,
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  });
  const contentType = res.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const err = new Error(typeof data === 'string' ? data : data?.detail || 'Request failed');
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const portalApi = {
  requestLogin: (email) => request('/api/auth/login', { method: 'POST', body: { email } }),
  verifyToken:  (token) => request('/api/auth/verify', { method: 'POST', body: { token } }),
  verifyCode:   (email, code) => request('/api/auth/verify-code', { method: 'POST', body: { email, code } }),
  me:           () => request('/api/auth/me'),
  logout:       () => request('/api/auth/logout', { method: 'POST' }),

  // ----- customer portal -----
  profile:      () => request('/api/portal/profile'),
  updateProfile:(payload) => request('/api/portal/profile', { method: 'PATCH', body: payload, csrf: true }),
  overview:     () => request('/api/portal/overview'),
  myRequests:   () => request('/api/portal/requests-detailed'),
  myWorkHistory:() => request('/api/portal/work-history'),
  myAppointments:() => request('/api/portal/appointments'),
  myQuotes:     () => request('/api/portal/quotes'),
  myInvoices:   () => request('/api/portal/invoices'),
  myProperties: () => request('/api/portal/properties'),
  requestProperty:(payload) => request('/api/portal/properties/request', { method: 'POST', body: payload, csrf: true }),
  myEquipment:  () => request('/api/portal/equipment'),
  myWarranties: () => request('/api/portal/warranties-detailed'),
  myMembership: () => request('/api/portal/membership'),
  requestMembershipChange: (payload) => request('/api/portal/membership/change-request', { method: 'POST', body: payload, csrf: true }),
  requestEmailChange: (new_email) => request('/api/portal/email-change/request', { method: 'POST', body: { new_email }, csrf: true }),
  confirmEmailChange: (token) => request('/api/portal/email-change/confirm', { method: 'POST', body: { token } }),

  // ----- admin -----
  adminDashboard: () => request('/api/admin/dashboard'),
  adminCustomers: (params = {}) => {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null)
    ).toString();
    return request(`/api/admin/customers${q ? `?${q}` : ''}`);
  },
  adminCustomer: (id) => request(`/api/admin/customers/${id}`),
  adminCustomerUpdate: (id, payload) => request(`/api/admin/customers/${id}`, { method: 'PATCH', body: payload, csrf: true }),
  adminCustomerNote: (id, note) => request(`/api/admin/customers/${id}/notes`, { method: 'POST', body: { note }, csrf: true }),
  adminCustomerLinkJobber: (id, jobber_client_id) => request(`/api/admin/customers/${id}/link-jobber`, { method: 'POST', body: { jobber_client_id }, csrf: true }),
  adminCustomerInvite: (id) => request(`/api/admin/customers/${id}/invite`, { method: 'POST', body: {}, csrf: true }),
  adminCustomerSuspend: (id, reason) => request(`/api/admin/customers/${id}/suspend`, { method: 'POST', body: { reason }, csrf: true }),
  adminCustomerReactivate: (id, reason) => request(`/api/admin/customers/${id}/reactivate`, { method: 'POST', body: { reason }, csrf: true }),
  adminCustomerArchive: (id, reason) => request(`/api/admin/customers/${id}/archive`, { method: 'POST', body: { reason }, csrf: true }),
  adminCustomerEmailChange: (id, new_email) => request(`/api/admin/customers/${id}/email-change`, { method: 'POST', body: { new_email }, csrf: true }),

  adminMemberships: (params = {}) => {
    const q = new URLSearchParams(Object.entries(params).filter(([,v]) => v)).toString();
    return request(`/api/admin/memberships${q ? `?${q}` : ''}`);
  },
  adminMembershipCreate: (payload) => request('/api/admin/memberships', { method: 'POST', body: payload, csrf: true }),
  adminMembershipUpdate: (id, payload) => request(`/api/admin/memberships/${id}`, { method: 'PATCH', body: payload, csrf: true }),

  adminWarranties: (params = {}) => {
    const q = new URLSearchParams(Object.entries(params).filter(([,v]) => v)).toString();
    return request(`/api/admin/warranties${q ? `?${q}` : ''}`);
  },
  adminWarrantyCreate: (payload) => request('/api/admin/warranties', { method: 'POST', body: payload, csrf: true }),
  adminWarrantyUpdate: (id, payload) => request(`/api/admin/warranties/${id}`, { method: 'PATCH', body: payload, csrf: true }),

  adminEquipmentCreate: (payload) => request('/api/admin/equipment', { method: 'POST', body: payload, csrf: true }),
  adminEquipmentList: (params = {}) => {
    const q = new URLSearchParams(Object.entries(params).filter(([,v]) => v)).toString();
    return request(`/api/admin/equipment${q ? `?${q}` : ''}`);
  },

  adminRequests: (params = {}) => {
    const q = new URLSearchParams(Object.entries(params).filter(([,v]) => v)).toString();
    return request(`/api/admin/requests${q ? `?${q}` : ''}`);
  },

  adminAudit: (limit = 100) => request(`/api/admin/audit?limit=${limit}`),
  adminAuditFiltered: (params = {}) => {
    const q = new URLSearchParams(Object.entries(params).filter(([,v]) => v)).toString();
    return request(`/api/admin/audit/filtered${q ? `?${q}` : ''}`);
  },

  adminCsvExportUrl: (params = {}) => {
    const q = new URLSearchParams(Object.entries(params).filter(([,v]) => v)).toString();
    return `${API}/api/admin/customers/export.csv${q ? `?${q}` : ''}`;
  },
};

export default portalApi;
